import React, { useContext, useEffect, useState } from "react";
import { Button, Row, Card, Col, Collapse, Table, Form } from "react-bootstrap";
import { abpnClient } from "../../../Services/AbpnClient";
import { paymentProcessor } from "../../../Services/PaymentProcessor/PaymentProcessor";
import Payment from "../../../Services/PaymentProcessor/Payment";
import { AuthContext } from "../../Auth/AuthProvider";
import Physician from "../../Physician/Models/Physician";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import ApplicationModel from "../Models/ApplicationModel";
import Examination from "../Models/Examination";
import { FeeType } from "../Models/Fee";
import { Alert } from 'react-bootstrap';
import { Fragment } from "react";
import { AbpnSpinner } from './../../Shared/Components/AbpnSpinner';
import { FattPayContainer } from "../../../Services/PaymentProcessor/FattPayContainer";
import ReceiptModel from "../../Shared/Models/ReceiptModel";
import InvoiceModel from "../../Shared/Models/InvoiceModel";
import InvoiceLineItemModel from "../../Shared/Models/InvoiceLineItemModel";
import { InvoiceStatus } from "../../Shared/Enums/InvoiceStatus";

interface IInvoicePaymentCardProps {
    application: ApplicationModel;
    physician: Physician;
    examination: Examination;
    isSectionAvailable: boolean;
    isSectionOpen: boolean;
    onApplicationSubmitted: (receipt: ReceiptModel) => void;
    examinationDates: string;
    displayBanner: () => void;
    onOpen: () => void;
    disabled: boolean;
    applicationDeadlineDate: string;
    totalBalance: number;
}

export function InvoicePaymentCard(props: IInvoicePaymentCardProps) {
    const hasPaymentPlan = false;
    const canSelectPaymentPlan = false;

    const [openCollapse, setOpenCollapse] = useState<boolean>(props.isSectionOpen);
    const [invoices, setInvoices] = useState<Array<InvoiceModel>>([]);
    const unpaidInvoices = invoices.filter(_ => _.isPaid == false);
    const unpaidLineItems = unpaidInvoices?.flatMap(_ => _.lineItems);
    const [isLoadingInvoices, setIsLoadingInvoices] = useState<boolean>(false);

    const contactAddress = props.physician.addresses.addresses.find(_ => _.isContact);

    const [payment, setPayment] = useState<Payment>({ email: props.physician.emailAddress, city: contactAddress?.city, state: contactAddress?.stateCode } as Payment);
    
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isPaymentSuccessful, setIsPaymentSuccessful] = useState<boolean>(null);
    const [isDisable, setIsDisable] = useState<boolean>(false);

    const payContainerRef = React.useRef(null);

    const hasInvoice = invoices.length > 0;

    useEffect(() => {
        const loadInvoices = async () => {
            setIsLoadingInvoices(true);

            //Get invoices related to the provided application.  I would really rather this component was passed the invoices as props.
            const results = (await abpnClient.get<Array<InvoiceModel>>('/api/invoice/getInvoicesForPhysician', authContext))
                .filter(_ => _.lineItems?.filter(x => x.product.examinationId == props.application.examinationId).length > 0);

            // if there are no results with an exam fee, we need to create an invoice.
            let isMissingInvoiceWithExamFee = results?.filter(_ => 
                _.lineItems?.filter(x => isExamFee(x.product.feeTypeId) && x.product.examinationId == props.application.examinationId).length > 0)
                .length == 0;
                
            if(isMissingInvoiceWithExamFee) {
                const newInvoice = await abpnClient.post<InvoiceModel>('/api/invoice/createInvoiceForApplication?applicationId=' + props.application.applicationId, null, authContext);
                results.push(newInvoice);
            }

            let newResults = new Array<InvoiceModel>();
            results.filter(x => x.isPaid == false && !(x.invoiceStatus == InvoiceStatus.Abandoned || x.invoiceStatus == InvoiceStatus.WrittenOff)).forEach(_ => {
                newResults.push(new InvoiceModel(_.invoiceNumber, _.createDate, _.isPaid, _.lineItems));
            });

            setInvoices(newResults);
            setIsLoadingInvoices(false);
        }

        if (!isLoadingInvoices && props.isSectionOpen) {
            loadInvoices();
        }
    }, [props.isSectionOpen])

    const balanceDue = invoices?.filter(_ => _.isPaid == false).reduce((sum, _) => sum + _.amountDue(), 0);
    const hasBalanceDue = balanceDue > 0;

    const authContext = useContext(AuthContext);

    const onPaymentChanged = (updated: Payment) => {
        setPayment(deepCopy(updated));
    }

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        setIsDisable(true);

        if (isSubmitting)
            return;

        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.classList.add('was-validated');

        // This is for when there is no payment due.
        if (payContainerRef === null || payContainerRef === undefined || !hasBalanceDue)
        {
                setIsSubmitting(true);
                let invoiceNumbers = invoices.map(_ => _.invoiceNumber);
                var invoiceReceipt = await abpnClient.post<ReceiptModel>('/api/invoice/completeInvoiceWithNoPayment',invoiceNumbers, authContext)
                await abpnClient.post(`/api/application/submitApplicationByExaminationId?examinationId=${props.application.examinationId}`,null, authContext);

            //Map untyped objects to typed ones.
            let newResults = new Array<InvoiceModel>();
            invoiceReceipt.invoices.forEach(_ => {
                newResults.push(new InvoiceModel(_.invoiceNumber, _.createDate, _.isPaid, _.lineItems));
            });

            invoiceReceipt.invoices = newResults;

            props.onApplicationSubmitted(invoiceReceipt);
            return;
        }

        if (payContainerRef.current.validate(payment) && event.currentTarget.checkValidity()) {
            event.currentTarget.classList.remove('was-validated');

            var updatedPayment = deepCopy(payment);
            updatedPayment = await paymentProcessor.tokenize(updatedPayment);

            updatedPayment.amount = invoices.reduce((sum, _) => sum + _.amountDue(), 0);

            setPayment(updatedPayment);

            if (!updatedPayment?.IsValid) {
                setIsDisable(false);
                return;
            }

            setIsDisable(false);
            setIsSubmitting(true);

            if (hasInvoice) {

                // This is where the invoice should be created and app submitted.
                let invoiceNumbers = invoices.map(_ => _.invoiceNumber);
                let invoiceReceipt = await abpnClient.post<ReceiptModel>(`/api/invoice/applyPaymentToInvoices?paymentToken=${updatedPayment.PaymentToken}&paymentAmount=${updatedPayment.amount}`, invoiceNumbers, authContext)
                await abpnClient.post(`/api/application/submitApplicationByExaminationId?examinationId=${props.application.examinationId}`,null, authContext);

                let reloadedInvoices = new Array<InvoiceModel>();
                invoiceReceipt.invoices.forEach(_ => {
                    reloadedInvoices.push(new InvoiceModel(_.invoiceNumber, _.createDate, _.isPaid, _.lineItems));
                });
                invoiceReceipt.invoices = reloadedInvoices;

                setIsSubmitting(false);
                setIsPaymentSuccessful(invoiceReceipt.isSuccessful);

                if (!invoiceReceipt.isSuccessful) {
                    return;
                }

                props.displayBanner();
                props.onApplicationSubmitted(invoiceReceipt);
                return;
            }
        }

        setIsDisable(false);
    }

    const isExamFee = (feeTypeId : number) => {
        return (feeTypeId == FeeType.examFeeMoc ||
                feeTypeId == FeeType.examFeeMocPrimaryInitial ||
                feeTypeId == FeeType.examFeeMocWithThreeOrMoreModules ||
                feeTypeId == FeeType.examFeeMocWithTwoModules ||
                feeTypeId == FeeType.examFeeSubspecialtyinitial)
    }

     const displayInvoiceLineItem = (item : InvoiceLineItemModel) => {
        return (<Fragment>
                    <tr key={item.product.feeTypeId}>
                        <td >{item.description}</td>
                        <td className="text-right">${item.unitPrice}</td>
                    </tr>
                    {displayInvoiceLineItemDiscount(item)}
                </Fragment>)
    }

    const displayInvoiceLineItemDiscount = (item : InvoiceLineItemModel) => {
        if (item.discountAmount > 0) return (
            <tr key={item.product.feeTypeId}>
                <td>{item.discountDescription}</td>
                <td className="text-right">$-{item.discountAmount}</td>
            </tr>
        );
        else return;
    } 

    useEffect(() => {
        setOpenCollapse(props.isSectionOpen);
    }, [props.isSectionOpen]);

    useEffect(() => {
        if (openCollapse) props.onOpen();
    }, [openCollapse]);

    return (
        <Card className="mb-4" border="pp-secondary-grey">
            <Card.Body>
                <Card.Title className="mb-0 font-weight-bolder" onClick={() => { if (!props.disabled) setOpenCollapse(!openCollapse) }}>
                    <span className={`text-pp-primary ${props.disabled && 'pp-disabled'}`}><i className="font-weight-bolder las la-angle-down"></i> Payment</span>
                </Card.Title>
                <Collapse in={openCollapse}>
                    <Form noValidate onSubmit={(e) => submit(e)}>
                        {isSubmitting ? <AbpnSpinner /> :
                            <Row className="mt-3">
                                <Col xs={12} md={4} lg={4}>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Applicant Name
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.physician.fullName}
                                    </Card.Text>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Email
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.physician.emailAddress}
                                    </Card.Text>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Examination Requested
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.examination.title}
                                    </Card.Text>
                                    <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                        Examination Dates
                                    </Card.Text>
                                    <Card.Text className="pp-font-size-smaller">
                                        {props.examinationDates}
                                    </Card.Text>

                                    {(invoices?.filter(_ => _.isPaid == false).length > 0) &&
                                    <Fragment>
                                        <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                            <div>Fees</div>
                                        </Card.Text>
                                        <Table variant="sm" className="pp-fee-table pp-font-size-smaller">
                                            <tbody>
                                                {unpaidLineItems.map(_ => { return (displayInvoiceLineItem(_))})}
                                                <tr>
                                                    <th className="font-weight-bolder">Total</th>
                                                    <td className="text-right">${balanceDue}</td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Fragment>}

                                    {(!canSelectPaymentPlan || hasPaymentPlan) && hasBalanceDue &&
                                        <Fragment>
                                            <FattPayContainer ref={payContainerRef} payment={payment} onPaymentChanged={onPaymentChanged} onPaymentTypeChanged={() => null} />
                                            {payment.paymentType && payment.IsValid === false &&

                                                <Alert className="pp-font-size-smaller" variant="pp-alert-danger">
                                                    <p><b>Could Not Validate Payment Method.</b> Please verify your payment method and try again</p>
                                                </Alert>}
                                            {isPaymentSuccessful === false &&
                                                <Alert className="pp-font-size-smaller" variant="pp-alert-danger">
                                                    <p><b>The provided payment method failed.</b> Please verify your payment method and try again</p>
                                                </Alert>}

                                        </Fragment>
                                    }

                                    {!isSubmitting &&
                                        <div>
                                            <Button type="submit"
                                                className="mt-4"
                                                variant="pp-primary"
                                                disabled={isDisable || (!payment.paymentType && hasBalanceDue)}>{`${(hasInvoice && hasBalanceDue) ? "Submit Payment" : "Submit Application"}`}
                                            </Button>
                                        </div>
                                    }
                                </Col>
                            </Row>
                        }
                    </Form>
                </Collapse>
            </Card.Body>
        </Card >
    );
}

export default InvoicePaymentCard;
